import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import validator from 'validator';
import { formatText } from 'core/i18n';
import StringHelper from 'utilities/stringHelper';
import { InfoTooltip } from 'components/tooltip/tooltip';
import { InputText } from 'components/inputText/inputText';
import { birthYearMatchesPassword, birthDayMatchesPassword } from 'commonsUtilities/validations';

const INITIAL_PASSWORD_STATE = {
    showShakeAnimationOnPasswordError: false,
    passNumeric: true,
    passAscDesc: true,
    passEquals: true,
    passBirthYear: true,
    passBirthDay: true,
    isValidPass: false,
};

export const Password = ({
    userProfile,
    userProfileForm,
    formActions,
    isValidPass,
    setIsValidPass,
    submitButtonValidations,
}) => {
    const [passwordState, setPasswordState] = useState(INITIAL_PASSWORD_STATE);
    const [showPassReq, setShowPassReq] = useState(false);
    const [visiblePassword, setVisiblePassword] = useState(false);
    const [iconPass, setIconPass] = useState('icon-pass-on show-password');
    const [inputType, setInputType] = useState('tel');
    const [isOnEye, setIsOnEye] = useState(false);
    const [passInputClass, setPassInputClass] = useState('password-number-input');

    useEffect(() => {
        // Auto remove the 'prospects-animation prospects-animation--shake' classes after 1 second or they wont trigger with subsequent errors
        if (passwordState.showShakeAnimationOnPasswordError) {
            const timer = setTimeout(() => {
                setPasswordState(prevState => ({ ...prevState, showShakeAnimationOnPasswordError: false }));
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [passwordState.showShakeAnimationOnPasswordError]);

    const handleShowPassReq = show => {
        setShowPassReq(show);
    };

    const handleHidePassword = () => {
        setInputType('tel');
        setPassInputClass('password-number-input');
        setIconPass('icon-pass-on show-password');

        if (isValidPass) {
            handleShowPassReq(false);
        }
    };

    const handleShowPassword = () => {
        setInputType('tel');
        setPassInputClass('');
        setIconPass('icon-pass-off show-password-off');
    };

    const handlePassword = () => {
        if (visiblePassword) {
            handleHidePassword();
        } else {
            handleShowPassword();
        }
        setVisiblePassword(!visiblePassword);
    };

    const handleIsOnEye = () => {
        setIsOnEye(true);
    };

    const handleIsOutEye = () => {
        setIsOnEye(false);
    };

    const handlePasswordRequirements = (...args) => {
        const [passNumeric, passAscDesc, passEquals, passBirthYear, passBirthDay] = args;
        const hasErrors = args.some(x => !x);

        setPasswordState({
            showShakeAnimationOnPasswordError: hasErrors,
            passNumeric,
            passAscDesc,
            passEquals,
            passBirthYear,
            passBirthDay,
            isValidPass: !hasErrors,
        });
    };

    const hiddenPasswordRequirements = classNames('col-sm-8 md-form-group password-requirements password-step', {
        'password-requirements--hidden': !showPassReq,
        'prospects-animation prospects-animation--shake': passwordState.showShakeAnimationOnPasswordError,
    });

    const setBirthDayMonthValidation = (formActions, valid) => {
        formActions.setValidity('userProfile.birthDay', { required: valid });
        formActions.setValidity('userProfile.birthMonth', { required: valid });
        formActions.setValidity('userProfile.birthDate', {
            validPinBirthDate: valid,
        });
    };

    const setBirthYearValidation = (formActions, valid) => {
        formActions.setValidity('userProfile.birthYear', { required: valid });
        formActions.setValidity('userProfile.birthDate', {
            validPinBirthDate: valid,
        });
    };

    const handleValidations = {
        required: value => {
            if (value && value.length) {
                return true;
            }
            if (!isOnEye) {
                handleShowPassReq(false);
            }

            return false;
        },
        valid: value => {
            const asc = '0123456789';
            const des = '9876543210';
            let valid = true;
            let passNumeric = true;
            let passAscDesc = true;
            let passEquals = true;
            const validYear = birthYearMatchesPassword(value, userProfile.birthYear);
            const validDayMonth = birthDayMatchesPassword(value, userProfile.birthDay, userProfile.birthMonth);
            const passBirthYear = !validYear;
            const passBirthDay = !validDayMonth;
            passBirthYear && setBirthYearValidation(formActions, true);
            passBirthDay && setBirthDayMonthValidation(formActions, true);
            valid = !(validYear || validDayMonth);

            if (value.length === 4) {
                if (!validator.matches(value, /^[0-9]{4}$/)) {
                    valid = false;
                    passNumeric = false;
                } else if (asc.includes(value) || des.includes(value)) {
                    valid = false;
                    passAscDesc = false;
                } else if (/^(\d)(?:\1)*$/.test(value)) {
                    valid = false;
                    passEquals = false;
                }
                if (!isOnEye) {
                    handleShowPassReq(!valid);
                }
            } else if (document.activeElement.id !== 'password') {
                valid = false;
            }
            handlePasswordRequirements(passNumeric, passAscDesc, passEquals, passBirthYear, passBirthDay);

            setIsValidPass(valid);

            return valid;
        },
    };

    return (
        <div className="row">
            <div className="col-sm-6 md-form-group">
                <InputText
                    profileModel="userProfile.password"
                    label={formatText('field-createPassword')}
                    type={inputType}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="password"
                    inputClass={passInputClass}
                    onlyDigits
                    maxLength={4}
                    id="password"
                    autoCompletePassword="off"
                    noFocusFromLabel
                    isPassword
                    placeholder={formatText('placeholder-password')}
                    validations={handleValidations}
                    onFocus={() => handleShowPassReq(true)}
                    onChange={event => {
                        event.target.value = StringHelper.getDigitsOnly(event.target.value);
                        if (!event.target.value) {
                            submitButtonValidations?.(false);
                            handlePasswordRequirements(true, true, true, true, true);
                        } else if (event.target.value.length >= 4) {
                            event.target.blur();
                        }
                    }}
                    messagesError={{
                        required: formatText('validation-fieldRequired'),
                    }}
                    selectTextOnFocus={false}
                    userProfileForm={userProfileForm}
                    userProfile={userProfile}
                    formActions={formActions}
                    tooltip={<InfoTooltip text={formatText('tooltip-password')} />}
                />
                <i
                    className={`icons icons--${iconPass} ${(showPassReq && 'no-valid') || 'valid'}`}
                    onMouseEnter={handleIsOnEye}
                    onMouseOut={handleIsOutEye}
                    onClick={handlePassword}
                    onTouchStart={handleShowPassword}
                    onTouchEnd={handleHidePassword}
                />
            </div>
            <div className={hiddenPasswordRequirements}>
                <h6 className="font-heading6">{formatText('field-Requirements')}</h6>
                <ul>
                    <li className={!passwordState.passAscDesc ? 'requirement-fail' : undefined}>
                        {formatText('field-passAscDesc')}
                    </li>
                    <li className={!passwordState.passEquals ? 'requirement-fail' : undefined}>
                        {formatText('field-passEquals')}
                    </li>
                    <li
                        className={
                            !passwordState.passBirthYear || !passwordState.passBirthDay ? 'requirement-fail' : undefined
                        }
                    >
                        {formatText('field-passBirthYear')}
                    </li>
                </ul>
            </div>
        </div>
    );
};

Password.propTypes = {
    userProfile: PropTypes.object,
    userProfileForm: PropTypes.object,
    formActions: PropTypes.object,
    isValidPass: PropTypes.bool,
    setBirthYearValidation: PropTypes.func,
    setBirthDayMonthValidation: PropTypes.func,
    setIsValidPass: PropTypes.func,
    submitButtonValidations: PropTypes.func,
};
